import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import { toast } from "sonner";
import { Plus, Sparkles } from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-secondary text-foreground",
  pending_approval: "bg-amber-100 text-amber-800",
  open: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
  closed: "bg-gray-200 text-gray-600",
};

export function StatusBadge({ status }) {
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${STATUS_STYLES[status] || "bg-secondary"}`}>
      {status?.replace("_", " ")}
    </span>
  );
}

const CREATOR_ROLES = ["hiring_manager", "recruiter", "group_hr_head", "entity_hr_head", "business_head", "super_admin"];

export default function Requisitions() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [reqs, setReqs] = useState([]);
  const [entities, setEntities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", entity_id: "", skills: "", experience_min: 2, experience_max: 5, openings: 1, priority: "medium", job_description: "", interview_rounds: "Screening Call, Technical Round, Managerial Round, HR Round" });
  const [jdBusy, setJdBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/requisitions").then((r) => setReqs(r.data));
  useEffect(() => {
    load();
    api.get("/master/entities").then((r) => {
      setEntities(r.data);
      if (r.data[0]) setForm((f) => ({ ...f, entity_id: r.data[0].id }));
    });
  }, []);

  const generateJD = async () => {
    if (!form.title) return toast.error("Enter a job title first");
    setJdBusy(true);
    try {
      const ent = entities.find((e) => e.id === form.entity_id);
      const { data } = await api.post("/requisitions/ai/generate-jd", {
        title: form.title, skills: form.skills, experience_min: +form.experience_min,
        experience_max: +form.experience_max, entity_name: ent?.name || "",
      });
      setForm((f) => ({ ...f, job_description: data.job_description }));
      toast.success("AI JD generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setJdBusy(false);
    }
  };

  const create = async () => {
    if (!form.title || !form.entity_id) return toast.error("Title and entity are required");
    setBusy(true);
    try {
      await api.post("/requisitions", {
        ...form, experience_min: +form.experience_min, experience_max: +form.experience_max, openings: +form.openings,
        interview_rounds: form.interview_rounds.split(",").map((r) => r.trim()).filter(Boolean),
      });
      toast.success("Requisition created as draft");
      setShowForm(false);
      setForm((f) => ({ ...f, title: "", skills: "", job_description: "" }));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const entName = (id) => entities.find((e) => e.id === id)?.name || "—";
  const canCreate = CREATOR_ROLES.includes(user?.role);

  return (
    <div data-testid="requisitions-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">Hiring Demand</div>
          <h1 className="font-heading font-black text-3xl tracking-tight mt-1">Requisitions</h1>
        </div>
        {canCreate && (
          <button data-testid="new-requisition-button" onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-[#0A251D] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">
            <Plus size={14} /> New Requisition
          </button>
        )}
      </div>

      {showForm && (
        <div className="border bg-white rounded-xl p-6 mt-5" data-testid="requisition-form">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Job Title *</label>
              <input data-testid="req-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 w-full border px-3 py-2 text-sm" placeholder="e.g. Senior Backend Engineer" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Entity *</label>
              <select data-testid="req-entity-select" value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })}
                className="mt-1 w-full border px-3 py-2 text-sm bg-white">
                {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Key Skills</label>
              <input data-testid="req-skills-input" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })}
                className="mt-1 w-full border px-3 py-2 text-sm" placeholder="Python, FastAPI, MongoDB" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Exp Min</label>
                <input type="number" value={form.experience_min} onChange={(e) => setForm({ ...form, experience_min: e.target.value })} className="mt-1 w-full border px-2 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Exp Max</label>
                <input type="number" value={form.experience_max} onChange={(e) => setForm({ ...form, experience_max: e.target.value })} className="mt-1 w-full border px-2 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Openings</label>
                <input type="number" value={form.openings} onChange={(e) => setForm({ ...form, openings: e.target.value })} className="mt-1 w-full border px-2 py-2 text-sm" />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Interview Workflow — rounds in order (comma-separated)</label>
            <input data-testid="req-rounds-input" value={form.interview_rounds} onChange={(e) => setForm({ ...form, interview_rounds: e.target.value })}
              className="mt-1 w-full border px-3 py-2 text-sm" />
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Job Description</label>
              <button data-testid="generate-jd-button" onClick={generateJD} disabled={jdBusy}
                className="flex items-center gap-1.5 text-xs font-semibold text-[#E05A47] border border-[#E05A47] px-3 py-1.5 hover:bg-[#E05A47] hover:text-white transition-colors duration-150 disabled:opacity-60">
                <Sparkles size={12} /> {jdBusy ? "Claude is writing…" : "Generate with AI"}
              </button>
            </div>
            <textarea data-testid="req-jd-textarea" value={form.job_description} onChange={(e) => setForm({ ...form, job_description: e.target.value })}
              rows={8} className="mt-2 w-full border px-3 py-2 text-sm font-mono" placeholder="Write manually or click Generate with AI" />
          </div>
          <div className="mt-4 flex gap-3">
            <button data-testid="req-save-button" onClick={create} disabled={busy}
              className="bg-[#0A251D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150 disabled:opacity-60">
              {busy ? "Saving…" : "Save Draft"}
            </button>
            <button onClick={() => setShowForm(false)} className="border px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-secondary transition-colors duration-150">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-5">
        <DataTable
          testId="requisitions-table"
          rows={reqs}
          searchKeys={["title", "skills", "created_by"]}
          pageSize={10}
          initialFilters={{
            ...(params.get("status") ? { status: params.get("status") } : {}),
            ...(params.get("entity") ? { entity_id: params.get("entity") } : {}),
          }}
          filters={[
            { key: "status", label: "Status", options: ["draft", "pending_approval", "open", "rejected", "closed"].map((s) => ({ value: s, label: s.replace("_", " ") })) },
            { key: "entity_id", label: "Entity", options: entities.map((e) => ({ value: e.id, label: e.name })) },
            { key: "priority", label: "Priority", options: ["high", "medium", "low"].map((p) => ({ value: p, label: p })) },
          ]}
          columns={[
            { key: "title", label: "Title", sortable: true, render: (r) => (
              <Link to={`/requisitions/${r.id}`} data-testid={`req-row-${r.id}`} className="font-semibold text-[#0A251D] hover:text-[#E05A47] transition-colors duration-100">{r.title}</Link>
            ) },
            { key: "entity_id", label: "Entity", sortable: true, sortValue: (r) => entName(r.entity_id), render: (r) => entName(r.entity_id) },
            { key: "openings", label: "Openings", sortable: true },
            { key: "experience_min", label: "Experience", sortable: true, render: (r) => `${r.experience_min}–${r.experience_max} yrs` },
            { key: "priority", label: "Priority", sortable: true, render: (r) => <span className="capitalize">{r.priority}</span> },
            { key: "status", label: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
            { key: "created_by", label: "Created by", sortable: true, render: (r) => <span className="text-muted-foreground">{r.created_by}</span> },
          ]}
        />
      </div>
    </div>
  );
}
